/**
 * Credit rates — the ONE place where "what does an action cost" is decided.
 *
 * Two files carry every number in the credits feature: this one (the credit
 * scale and the flat per-event charges) and `llm-pricing.ts` (USD per model
 * token). Nothing else in the repo may hard-code a credit constant; a unit
 * test greps for stray `50_000` / `CREDITS_PER_USD` copies.
 *
 * Credits are stored in the ledger as integer *micro-credits* so a fraction
 * of a cent of model cost is not rounded away: 1 credit = 1_000_000 micro.
 * Charges are frozen at booking time, so changing a rate here only affects
 * rows written afterwards.
 */

/** 1 USD of model spend = 100 credits. */
export const CREDITS_PER_USD = 100

/** Credit limit every organization starts with (migration default too). */
export const DEFAULT_ORG_CREDIT_LIMIT = 50_000

/** Ledger fixed-point scale: 1 credit = 1_000_000 micro-credits. */
export const CREDITS_MICRO = 1_000_000

/** Flat credit charges per event (not token-based). */
export const FLAT_CHARGES = {
  /** Any completed agent tool call not matched by TOOL_CHARGES. */
  tool: 5,
  /** The `web_search` tool — replaces the generic tool charge. */
  web_search: 1,
  /** Every `browser*` tool step. */
  browser: 1,
  /** One generated audio (TTS cache miss; cache hits are free). */
  tts: 2,
  /** One dictation session (one issued scribe token). */
  stt: 2,
  /**
   * PLAN-085 — one participant-minute of a HUB video meeting. RealtimeKit
   * charges $0.002/min (= 0.2 credits); the rest is the usual margin.
   */
  meeting_participant_minute: 0.3,
  /**
   * One transcribed audio-minute. Whisper large-v3-turbo is $0.00051/min
   * (= 0.051 credits); same margin shape as the other flat charges.
   */
  meeting_audio_minute: 0.1,
  /**
   * One recorded minute (board m5gc #8). RealtimeKit's recording costs
   * ~$0.008/min (= 0.8 credits); booked at 1 credit/min (Andreas, 2026-09-15).
   */
  meeting_recording_minute: 1,
  /**
   * Board card loap — one started minute of async file transcription
   * (uploaded audio/video → transcript document). Soniox async is $0.10/h
   * (≈ 0.17 credits/h); booked like `meeting_audio_minute`.
   */
  transcription_minute: 0.1,
  /**
   * Board card 2u86 — the AI description of one image document.
   * gemini-2.5-flash-lite reads a 1568-px picture for ≈ $0.0003 (≈ 0.03
   * credits); booked like the other flat charges.
   */
  image_description: 0.1,
  /**
   * Board card wwfu — one completed call of a project plugin (remote MCP)
   * tool. The remote call runs on the customer's own account and costs HUB
   * nothing; the model tokens are billed as usual, so this is a token fee
   * instead of the generic tool charge (a 6-call plugin turn would cost 30).
   */
  mcp: 1,
} as const

/** Ledger row kinds — `kind` column of `credit_ledger` (no CHECK constraint). */
export type CreditKind =
  | 'llm'
  | 'tool'
  | 'web_search'
  | 'tts'
  | 'stt'
  /** PLAN-085: RealtimeKit participant minutes of a HUB meeting. */
  | 'meeting_rtk'
  /** PLAN-085: Whisper transcription of a HUB meeting's audio. */
  | 'meeting_stt'
  /** Board m5gc #8: RealtimeKit recording minutes of a HUB meeting. */
  | 'meeting_recording'
  /** Board loap: async transcription of an uploaded audio/video file. */
  | 'transcription'
  /** Board 2u86: the AI description of an uploaded picture. */
  | 'image_description'

/**
 * Per-tool overrides. Exact names are matched first, then the regex rules;
 * an unlisted tool falls back to `FLAT_CHARGES.tool`.
 */
export const TOOL_CHARGES: Array<{
  match: string | RegExp
  credits: number
  kind: Extract<CreditKind, 'tool' | 'web_search'>
}> = [
  { match: 'web_search', credits: FLAT_CHARGES.web_search, kind: 'web_search' },
  // browserNavigate, browserRead, browserClick, browserClose, …
  { match: /^browser[A-Z]/, credits: FLAT_CHARGES.browser, kind: 'tool' },
  // Project plugins (wwfu): mcp__<slug>__<tool>
  { match: /^mcp__/, credits: FLAT_CHARGES.mcp, kind: 'tool' },
]

/** Rate used when a model is unknown to `llm-pricing.ts` — Opus-class, i.e. err expensive. */
export const FALLBACK_LLM_USD_PER_MTOK = { input: 5, output: 25 } as const

/** What one completed tool call costs, and which ledger kind it books as. */
export function toolCharge(toolName: string): {
  credits: number
  kind: Extract<CreditKind, 'tool' | 'web_search'>
} {
  for (const rule of TOOL_CHARGES) {
    if (typeof rule.match === 'string') {
      if (rule.match === toolName) return { credits: rule.credits, kind: rule.kind }
    } else if (rule.match.test(toolName)) {
      return { credits: rule.credits, kind: rule.kind }
    }
  }
  return { credits: FLAT_CHARGES.tool, kind: 'tool' }
}

/** USD of model spend → credits (never negative, never NaN). */
export function usdToCredits(usd: number): number {
  if (!Number.isFinite(usd) || usd <= 0) return 0
  return usd * CREDITS_PER_USD
}

/** Credits → the USD they represent. */
export function creditsToUsd(credits: number): number {
  if (!Number.isFinite(credits) || credits <= 0) return 0
  return credits / CREDITS_PER_USD
}

/** Credits → the integer micro-credits stored in the ledger. */
export function toMicro(credits: number): number {
  if (!Number.isFinite(credits) || credits <= 0) return 0
  return Math.round(credits * CREDITS_MICRO)
}

/** Ledger micro-credits → credits. */
export function fromMicro(micro: number): number {
  if (!Number.isFinite(micro) || micro <= 0) return 0
  return micro / CREDITS_MICRO
}

/**
 * Display form: whole credits above 10, one decimal below — a single LLM
 * turn on the cheap model is a fraction of a credit and must not read "0".
 */
export function formatCredits(credits: number, locale?: string): string {
  const value = Number.isFinite(credits) && credits > 0 ? credits : 0
  const digits = value === 0 || value >= 10 ? 0 : value >= 1 ? 1 : 2
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(value)
}

// ── Limit decision (shared by the Worker's budget check and the sidebar bar) ─

/** Which limit stopped the action — `null` means there is budget left. */
export type CreditBlockReason = 'org' | 'project'

export interface CreditUsageSnapshot {
  orgUsed: number
  creditLimit: number
  projectUsed: number
  /** Project cap, or null when the project only draws on the org budget. */
  projectCap: number | null
}

/**
 * Blocked when the org budget is spent, or when a capped project has spent
 * its cap. Checked at turn start / TTS generation / dictation start only —
 * never mid-turn, so a running reply always finishes.
 *
 * Limits are always ≥ 1 (the contract enforces it), so there is no "0 means
 * unlimited" escape hatch: a limit the usage has reached blocks, full stop.
 */
export function creditBlockReason(usage: CreditUsageSnapshot): CreditBlockReason | null {
  if (usage.orgUsed >= usage.creditLimit) return 'org'
  if (usage.projectCap !== null && usage.projectUsed >= usage.projectCap) return 'project'
  return null
}

/** 0–1 fill of the sidebar bar: the tightest limit that applies. */
export function creditUsageRatio(usage: CreditUsageSnapshot): number {
  const [used, limit] =
    usage.projectCap !== null
      ? [usage.projectUsed, usage.projectCap]
      : [usage.orgUsed, usage.creditLimit]
  if (!Number.isFinite(limit) || limit <= 0) return 1
  return Math.min(1, Math.max(0, used / limit))
}

/** Bar turns amber at 80 % (D7) and red once the limit is reached. */
export const CREDIT_WARN_RATIO = 0.8
